// openInstallableModal — Phase 4 real implementation.
//
// Registered as a shared function so el.call('openInstallableModal', opts)
// resolves from InstallableRail.js, InstallableRailItem.js, canvas tiles,
// dashboard, admin, and any other surface that needs to trigger the
// MarketplaceInstallModal without owning a marketplace island.
//
// Replaces the Phase 3 stub (which only console.warn'd + set root.state._installableModal).
//
// Delegation strategy:
//   1. Try to find a marketplace host via the marketplaceHostOf walk
//      (works when called from inside or adjacent to a MarketplaceIsland).
//   2. Fall back to root.state when outside a marketplace island — the
//      InstallableModalStub mounted at the app root also listens to
//      root.state._installableModal so it can show a fallback modal.
//   3. When a marketplace host IS found, delegate directly to the full
//      MarketplaceInstallModal via host.update({ installModal: ... }).
//
// Args:
//   domain        – 'integration'|'library'|'mcp'|'plugin'|'template'
//   kind          – kind key string (= preselectedKind), e.g. 'github'
//   installed     – boolean (true = manage flow, false = install flow)
//   defaultScope  – { type: 'org'|'workspace'|'project', id: string }
//   catalogFilter – filter object (category, applicable_to_types, applicable_to_scopes)
//   onInstalled   – (instance) => void callback
//
// Contract is the same as marketplace's openInstallableModal — this module
// is the single shared registration point; the marketplace package contains
// the full logic.

// Walk up to find a marketplace host element (same algorithm as
// marketplaceHostOf in marketplace/functions/index.js, duplicated
// here to avoid cross-package imports).
const _findMarketplaceHost = (el) => {
  let cur = el
  let lastWithState = null
  while (cur) {
    if (cur._marketplaceHost && cur.state) return cur.state
    if (cur.state) lastWithState = cur
    if (!cur.parent) break
    cur = cur.parent
  }
  return lastWithState?.state || el?.state || null
}

// Resolve item from catalogue by kind slug.
// Mirrors the slug-matching logic in marketplace/functions/index.js
// so the lookup works regardless of which package this is called from.
const _slugForId = (id) =>
  String(id || '').replace(/^(int|reg|plg|tpl|lib)\./, '').replace(/-/g, '_')

const _findItemByKind = (catalog, kind) => {
  if (!Array.isArray(catalog) || !kind) return null
  return catalog.find((i) => {
    const s = _slugForId(i.id)
    return (
      s === kind ||
      i.id === kind ||
      i.id === `int.${kind}` ||
      i.id === `plg.${kind}` ||
      i.id === `lib.${kind}` ||
      i.id === `tpl.${kind}`
    )
  }) || null
}

// Check catalogFilter against an item. Returns error string or null.
const _checkCatalogFilter = (item, catalogFilter) => {
  if (!catalogFilter || !item) return null
  if (catalogFilter.category && item.category !== catalogFilter.category) {
    return `This item is in the "${item.category}" category, not "${catalogFilter.category}".`
  }
  if (Array.isArray(catalogFilter.applicable_to_types) && catalogFilter.applicable_to_types.length > 0) {
    const itemTypes = item._liveKind?.applicable_to_types || []
    const overlap = catalogFilter.applicable_to_types.some((t) => itemTypes.includes(t))
    if (!overlap) {
      return `This item does not apply to the required type(s): ${catalogFilter.applicable_to_types.join(', ')}.`
    }
  }
  if (Array.isArray(catalogFilter.applicable_to_scopes) && catalogFilter.applicable_to_scopes.length > 0) {
    const itemScopes = item._liveKind?.applicable_to_scopes || []
    const overlap = catalogFilter.applicable_to_scopes.some((s) => itemScopes.includes(s))
    if (!overlap) {
      return `This item does not apply to the required scope(s): ${catalogFilter.applicable_to_scopes.join(', ')}.`
    }
  }
  return null
}

// eslint-disable-next-line func-style
export function openInstallableModal (opts) {
  const el = this

  const domain = opts?.domain || null
  const kind = opts?.kind || opts?.preselectedKind || null
  const installed = !!opts?.installed
  const defaultScope = opts?.defaultScope || null
  const catalogFilter = opts?.catalogFilter || null
  const onInstalled = typeof opts?.onInstalled === 'function' ? opts.onInstalled : null

  // 1. Walk element tree to find a MarketplaceIsland host.
  let host = null
  try { host = _findMarketplaceHost(el) } catch {}

  // 2. Fall back to root.state when outside a marketplace island.
  if (!host) {
    try {
      const root = el.getRoot?.()
      if (root?.state) host = root.state
    } catch {}
  }

  if (!host) {
    console.warn('[openInstallableModal] Could not resolve host state. opts:', opts)
    return
  }

  const catalog = host.catalog || []

  // 3. Resolve the item from catalogue.
  let item = null
  if (kind) {
    item = _findItemByKind(catalog, kind)

    if (!item) {
      // Kind not found in catalogue — open modal with catalogError.
      host.update?.({
        installModal: {
          item: { name: kind, id: kind, kind: domain || 'integration' },
          step: 'config',
          draft: {},
          domain,
          defaultScope,
          catalogFilter,
          onInstalled,
          _catalogError:
            `"${kind}" is not available in the current catalogue. ` +
            'See /admin-integration-kinds if you need to add it.'
        },
        _installModalError: null
      })
      return
    }

    // 4. Validate catalogFilter against the found item.
    const filterError = _checkCatalogFilter(item, catalogFilter)
    if (filterError) {
      host.update?.({
        installModal: {
          item,
          step: 'config',
          draft: {},
          domain,
          defaultScope,
          catalogFilter,
          onInstalled,
          _catalogError: filterError
        },
        _installModalError: null
      })
      return
    }
  }

  // 5. Build initial draft — apply defaultScope if provided.
  const draft = {}
  if (defaultScope?.type) {
    draft.scopeType = defaultScope.type
    if (defaultScope.id) draft.scopeId = defaultScope.id
  } else if (item?._liveKind?.default_scope) {
    draft.scopeType = item._liveKind.default_scope
  }

  // 6. Determine opening step.
  const openStep = (defaultScope || installed) ? 'config' : 'scope'

  host.update?.({
    installModal: {
      item: item || null,
      step: openStep,
      draft,
      domain,
      defaultScope,
      catalogFilter,
      onInstalled,
      _catalogError: null
    },
    _installModalError: null
  })
}
